import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isMenuPage = location.pathname === '/menu';
  
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  
  useEffect(() => {
    const handleScroll = () => {
      // Set scrolled based on scroll position
      setScrolled(window.scrollY > 50);
      
      // Only track active section on homepage
      if (isHomePage) {
        // Determine active section based on scroll position
        const sections = ['hero', 'intro', 'locations', 'catering', 'social'];
        for (const id of sections.reverse()) {
          const element = document.getElementById(id);
          if (element && window.scrollY >= element.offsetTop - 100) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);
  
  const smoothScrollTo = (id: string) => {
    setMobileMenuOpen(false);
    
    if (isHomePage) {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  // Determine if header should have background (scrolled or non-home page)
  const shouldHaveBackground = scrolled || isBlogPage || isMenuPage;
  
  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldHaveBackground ? 'bg-background/90 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <motion.img 
            src="/assets/images/optimized/ThaprachanLogo.webp" 
            alt="Thaprachan Thai" 
            className="h-16"
            whileHover={{ scale: 1.05 }}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {isHomePage ? (
          <AnchorLink href="#intro" onClick={() => smoothScrollTo('intro')} scrolled={shouldHaveBackground} isActive={activeSection === 'intro'}>About</AnchorLink>
          ) : (
          <Link 
              to="/#intro"
              className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
              }`}
            >
              About
            </Link>
          )}
          
          <Link
            to="/menu"
            className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
              shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
            } ${isMenuPage ? 'after:w-full' : ''}`}
          >
            Menu
          </Link>
          
          {isHomePage ? (
            <AnchorLink href="#locations" onClick={() => smoothScrollTo('locations')} scrolled={shouldHaveBackground} isActive={activeSection === 'locations'}>Locations</AnchorLink>
          ) : (
            <Link 
              to="/#locations"
              className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
              }`}
            >
              Locations
            </Link>
          )}
          
          {isHomePage ? (
            <AnchorLink href="#catering" onClick={() => smoothScrollTo('catering')} scrolled={shouldHaveBackground} isActive={activeSection === 'catering'}>Catering</AnchorLink>
          ) : (
            <Link 
              to="/#catering"
              className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
              }`}
            >
              Catering
            </Link>
          )}
          
          {isHomePage ? (
            <AnchorLink href="#social" onClick={() => smoothScrollTo('social')} scrolled={shouldHaveBackground} isActive={activeSection === 'social'}>Gallery</AnchorLink>
          ) : (
            <Link 
              to="/#social"
              className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
                shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
              }`}
            >
              Gallery
            </Link>
          )}
          
          <Link 
            to="/blog"
            className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
              shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
            } ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'after:w-full' : ''}`}
          >
            Blog
          </Link>
          
          <Link 
            to="/near-uw"
            className={`font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${
              shouldHaveBackground ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
            } ${location.pathname === '/near-uw' ? 'after:w-full' : ''}`}
          >
            UW Students
          </Link>
          
          {/* External Links */}
          <motion.a
            href="tel:+12065233938"
            data-track="call_click"
            className={`px-6 py-2 bg-transparent rounded-sm font-medium transition-colors ${
              shouldHaveBackground
                ? 'border border-primary text-primary-ink hover:bg-primary/10'
                : 'border border-white text-white hover:bg-white/10'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Reservations
          </motion.a>
          
          <motion.a
            href="https://order.thaprachanthaicuisine.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-track="order_click"
            className="px-6 py-2 bg-primary text-text rounded-sm font-semibold hover:bg-primary/90 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Order Online
          </motion.a>
        </nav>
        
        {/* Mobile menu button */}
        {/* -m-2 keeps the icon where it was while p-2 grows the hit area from
            24px to 44px. It's the only nav control on a phone. */}
        <button
          className={`md:hidden p-2.5 -m-2.5 ${shouldHaveBackground ? 'text-primary-ink' : 'text-white'}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
          aria-expanded={mobileMenuOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </>
            )}
          </svg>
        </button>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div 
          className="md:hidden bg-background py-4 shadow-lg"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="container mx-auto px-4 flex flex-col space-y-4">
            {isHomePage ? (
            <AnchorLink 
              href="#intro" 
              onClick={() => smoothScrollTo('intro')}
              className="py-2 border-b border-gray-200"
              scrolled={true}
              isActive={activeSection === 'intro'}
            >
              About
            </AnchorLink>
            ) : (
            <Link 
                to="/#intro"
                className="py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
            )}
            
            <Link
              to="/menu"
              className={`py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-1px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${isMenuPage ? 'text-primary-ink after:w-full' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Menu
            </Link>
            
            {isHomePage ? (
            <AnchorLink 
              href="#locations" 
              onClick={() => smoothScrollTo('locations')}
              className="py-2 border-b border-gray-200"
              scrolled={true}
              isActive={activeSection === 'locations'}
            >
              Locations
            </AnchorLink>
            ) : (
              <Link 
                to="/#locations"
                className="py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Locations
              </Link>
            )}
            
            {isHomePage ? (
            <AnchorLink 
              href="#catering" 
              onClick={() => smoothScrollTo('catering')}
              className="py-2 border-b border-gray-200"
              scrolled={true}
              isActive={activeSection === 'catering'}
            >
              Catering
            </AnchorLink>
            ) : (
              <Link 
                to="/#catering"
                className="py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Catering
              </Link>
            )}
            
            {isHomePage ? (
            <AnchorLink 
              href="#social" 
              onClick={() => smoothScrollTo('social')}
              className="py-2 border-b border-gray-200"
              scrolled={true}
              isActive={activeSection === 'social'}
            >
              Gallery
            </AnchorLink>
            ) : (
              <Link 
                to="/#social"
                className="py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Gallery
              </Link>
            )}
            
            <Link 
              to="/blog"
              className={`py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-1px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'text-primary-ink after:w-full' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            
            <Link 
              to="/near-uw"
              className={`py-2 border-b border-gray-200 text-text hover:text-primary-ink font-medium transition-colors relative hover:after:w-full after:absolute after:bottom-[-1px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 ${location.pathname === '/near-uw' ? 'text-primary-ink after:w-full' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              UW Students
            </Link>
            
            <div className="flex flex-col space-y-3 pt-2">
              <a
                href="tel:+12065233938"
                data-track="call_click"
                className="py-2 px-4 border border-primary text-primary-ink rounded-sm text-center font-medium"
              >
                Reservations
              </a>
              
              <a
                href="https://order.thaprachanthaicuisine.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-track="order_click"
                className="py-2 px-4 bg-primary text-text rounded-sm text-center font-semibold"
              >
                Order Online
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

interface AnchorLinkProps {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  scrolled?: boolean;
  isActive?: boolean;
}

const AnchorLink = ({ href, onClick, children, className = '', scrolled = false, isActive = false }: AnchorLinkProps) => {
  return (
    <a 
      href={href} 
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick();
      }}
      className={`font-medium transition-colors relative ${
        scrolled ? 'text-text hover:text-primary-ink' : 'text-white hover:text-primary/90'
      } ${className} ${isActive 
        ? 'after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-primary after:transition-all after:duration-500' 
        : 'hover:after:w-full after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300'}`}
    >
      {children}
    </a>
  );
};

export default Header; 